import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import {Menu, X, Instagram, Facebook} from 'lucide-react'
import { nav, footer, links } from '@/content/site'

function Layout() {
  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const location = useLocation()

  useEffect(() => {
    setOpen(false)
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })
  }, [location.pathname])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12)
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <div className="min-h-screen bg-paper-grain text-[#1F1B1D]">
      {/* NAV */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-[#F6EFF4]/85 backdrop-blur-md border-b border-[#DCC9D2]/60' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="https://static.lumi.new/6b/6b5b5b650b83af1de0c03aca0be58af8.webp"
              alt={nav.brand}
              className="h-[50px] w-auto object-contain"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {nav.items.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `text-[13px] tracking-wide transition-colors ${
                    isActive ? 'text-[#9E3A5A]' : 'text-[#3A3236] hover:text-[#1F1B1D]'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <a
              href={links.booking}
              target="_blank"
              rel="noreferrer"
              className="text-[13px] tracking-wide px-5 py-2.5 border border-[#9E3A5A] text-[#9E3A5A] hover:bg-[#9E3A5A] hover:text-[#F6EFF4] transition-colors duration-500"
            >
              {nav.ctaButton}
            </a>
          </nav>

          <button
            type="button"
            onClick={() => setOpen(true)}
            className="lg:hidden p-2 -mr-2"
            aria-label="Ouvrir le menu"
          >
            <Menu size={22} />
          </button>
        </div>
      </header>

      {/* MOBILE MENU */}
      <div
        className={`fixed inset-0 z-[60] bg-[#F6EFF4] transition-all duration-500 lg:hidden ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="h-20 px-6 flex items-center justify-between border-b border-[#DCC9D2]/60">
          <img
            src="https://static.lumi.new/6b/6b5b5b650b83af1de0c03aca0be58af8.webp"
            alt={nav.brand}
            className="h-[50px] w-auto object-contain"
          />
          <button type="button" onClick={() => setOpen(false)} aria-label="Fermer le menu" className="p-2 -mr-2">
            <X size={22} />
          </button>
        </div>
        <nav className="px-6 py-10 flex flex-col gap-6">
          {nav.items.map((item, i) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `font-serif-display text-3xl ${isActive ? 'text-[#9E3A5A]' : 'text-[#1F1B1D]'}`
              }
              style={{ animationDelay: `${i * 60}ms` }}
            >
              {item.label}
            </NavLink>
          ))}
          <a
            href={links.booking}
            target="_blank"
            rel="noreferrer"
            className="mt-6 text-center px-6 py-4 border border-[#9E3A5A] text-[#9E3A5A] text-[13px] tracking-widest uppercase"
          >
            {nav.ctaButton}
          </a>
        </nav>
      </div>

      <main>
        <Outlet />
      </main>

      {/* FOOTER */}
      <footer className="border-t border-[#DCC9D2] bg-[#EFE5EC]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="font-serif-display text-3xl mb-6">{footer.brand}</div>
            <p className="font-serif-display italic text-xl text-[#3A3236] leading-relaxed max-w-md">
              {footer.quote}
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="text-[11px] tracking-[0.25em] uppercase text-[#C0923F] mb-5">{footer.navigateLabel}</div>
            <ul className="space-y-3 text-[14px]">
              {nav.items.map(item => (
                <li key={item.to}>
                  <Link to={item.to} className="link-underline text-[#3A3236] hover:text-[#1F1B1D]">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-[11px] tracking-[0.25em] uppercase text-[#C0923F] mb-5">{footer.contactLabel}</div>
            <ul className="space-y-3 text-[14px] text-[#3A3236]">
              <li>
                <a href={`mailto:${links.email}`} className="link-underline">{links.email}</a>
              </li>
              <li>
                <a href={`tel:${links.phone.replace(/\s/g, '')}`} className="link-underline">{links.phone}</a>
              </li>
              <li>
                <a
                  href={links.instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 link-underline"
                >
                  <Instagram size={14} /> heartcanvas.art
                </a>
              </li>
              <li>
                <a
                  href={links.facebook}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 link-underline"
                >
                  <Facebook size={14} /> heartcanvas.art
                </a>
              </li>
              <li className="pt-2 text-[12px] text-[#3A3236]/70">
                {footer.artistMention} <a href={links.amanaliya} target="_blank" rel="noreferrer" className="link-underline">amanaliya.com</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-[#DCC9D2]">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[12px] text-[#3A3236]/70">
            <span>© {new Date().getFullYear()} {footer.copyright}</span>
            <span className="tracking-wider uppercase text-[10px]">{footer.tagline}</span>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Layout
